import logger from "../utils/logger";
import { apiClient } from "../lib/apiClient";

export interface TTSApiOptions {
  lang: string;
}

// Add playTTS function for Smart Read
export async function playTTS(text: string, language: string): Promise<void> {
  const ttsService = new TTSApiService();
  return ttsService.speak(text, { lang: language });
}

export class TTSApiService {
  private audio: HTMLAudioElement | null = null;
  private isFirefox: boolean = false;

  constructor() {
    // Initialize audio element
    if (typeof window !== "undefined") {
      this.audio = new Audio();

      // Detect Firefox
      this.isFirefox =
        navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
      if (this.isFirefox) {
        logger.info("TTSApiService - Firefox browser detected");
      }
    }
  }

  async speak(text: string, options: TTSApiOptions): Promise<void> {
    try {
      // Log the language being used
      logger.info(
        `TTSApiService - Using language: ${options.lang} for text: ${text.substring(0, 30)}...`,
      );

      // Use apiClient to get TTS audio
      const data = await apiClient.tts(text, options.lang);

      logger.info(
        `TTSApiService - Received response with success=${data.success}, format=${data.format}, audio length=${data.audio ? data.audio.length : "undefined"}`,
      );

      if (!data.success || !data.audio) {
        throw new Error("Invalid response from TTS service");
      }

      // Check if the audio data is valid
      if (data.audio.length < 100) {
        logger.warn(
          `TTSApiService - Audio data is suspiciously short: ${data.audio.length} chars`,
        );
      }

      // Make sure we have a valid format
      const audioFormat = data.format || "mp3";

      // Firefox requires different handling
      if (this.isFirefox) {
        return this.handleFirefoxAudio(data.audio, audioFormat);
      } else {
        return this.handleStandardAudio(data.audio, audioFormat);
      }
    } catch (error) {
      logger.error("TTS API error:", error);
      throw error;
    }
  }

  private async handleFirefoxAudio(
    audioData: string,
    format: string,
  ): Promise<void> {
    logger.info("TTSApiService - Using Firefox-specific audio handling");

    try {
      // Use data URL directly for Firefox (Firefox has issues with blob URLs for audio)
      const audioUrl = `data:audio/${format};base64,${audioData}`;

      if (!this.audio) {
        throw new Error("Audio playback is not supported in this environment");
      }

      // Add event listeners for debugging
      this.audio.onloadeddata = () =>
        logger.info("TTSApiService - Firefox: Audio loaded");
      this.audio.onplay = () =>
        logger.info("TTSApiService - Firefox: Audio playback started");
      this.audio.onerror = (e) => {
        logger.error("TTSApiService - Firefox: Audio error:", e);
        // Try alternative method if there's an error
        this.tryAlternativePlayback(audioData, format);
      };

      // Stop any currently playing audio
      this.audio.pause();
      this.audio.currentTime = 0;

      // Set new source
      this.audio.src = audioUrl;

      // Firefox sometimes needs a small delay
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Play the audio
      try {
        await this.audio.play();
      } catch (playError) {
        logger.error("TTSApiService - Firefox play error:", playError);
        // Try alternative method
        await this.tryAlternativePlayback(audioData, format);
      }
    } catch (error) {
      logger.error("TTSApiService - Firefox audio handling error:", error);
      throw error;
    }
  }

  private async handleStandardAudio(
    audioData: string,
    format: string,
  ): Promise<void> {
    try {
      // Clean up the base64 string to handle potential padding issues
      const base64String = audioData.replace(/^data:audio\/\w+;base64,/, "");
      const sanitizedBase64 = base64String.replace(/[^A-Za-z0-9+/=]/g, "");

      // Convert base64 to raw binary data
      let byteCharacters;
      try {
        byteCharacters = atob(sanitizedBase64);
      } catch (atobError) {
        logger.error("TTSApiService - Error decoding base64:", atobError);
        throw new Error("Invalid base64 audio data");
      }

      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, { type: `audio/${format}` });
      const audioUrl = URL.createObjectURL(blob);

      logger.info(`TTSApiService - Created audio blob URL: ${audioUrl}`);

      if (!this.audio) {
        throw new Error("Audio playback is not supported in this environment");
      }

      // Add event listeners for debugging
      this.audio.onloadeddata = () =>
        logger.info("TTSApiService - Audio loaded");
      this.audio.onplay = () =>
        logger.info("TTSApiService - Audio playback started");
      this.audio.onerror = (e) =>
        logger.error("TTSApiService - Audio error:", e);

      // Stop any currently playing audio
      this.audio.pause();
      this.audio.currentTime = 0;

      // Set new source
      this.audio.src = audioUrl;

      try {
        await this.audio.play();
      } catch (playError) {
        logger.error("TTSApiService - Play error:", playError);

        // Fallback to data URL approach
        logger.info("TTSApiService - Falling back to data URL approach");
        const dataUrl = `data:audio/${format};base64,${audioData}`;

        this.audio.src = dataUrl;
        await this.audio.play();
      }
    } catch (error) {
      logger.error("TTSApiService - Standard audio handling error:", error);
      throw error;
    }
  }

  private async tryAlternativePlayback(
    audioData: string,
    format: string,
  ): Promise<void> {
    logger.info("TTSApiService - Trying alternative playback method");

    try {
      // Create a new audio element - sometimes the cached one has issues
      const newAudio = new Audio();
      const audioUrl = `data:audio/${format};base64,${audioData}`;

      newAudio.src = audioUrl;
      await newAudio.play();

      // Keep track of the new audio element to be able to stop it later
      this.audio = newAudio;
    } catch (error) {
      logger.error("TTSApiService - Alternative playback failed:", error);
      throw error;
    }
  }

  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
  }

  isSpeaking(): boolean {
    return this.audio ? !this.audio.paused : false;
  }
}
